'use client'

import React, { useEffect, useRef, useState } from 'react'

import { Box, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Checkbox } from '../Checkbox'
import { Tooltip } from '../Tooltip/Tooltip'
import { checkboxOptionRowStyles } from '../TypeaheadSearch/TypeaheadSearch.styles'
import {
  rowCaptionStyles,
  rowLabelStyles,
  rowSubtitleStyles,
} from './CheckboxFilterList.styles'
import type { CheckboxFilterOption } from './CheckboxFilterList.types'

type CheckboxFilterListRowProps = {
  option: CheckboxFilterOption
  isSelected: boolean
  onToggle: (value: string) => void
  colorScheme: ColorScheme
}

export const CheckboxFilterListRow = ({
  option,
  isSelected,
  onToggle,
  colorScheme,
}: CheckboxFilterListRowProps) => {
  const labelRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null)

  // The label is a single ellipsised line; the tooltip only appears when the
  // text actually overflows it. Width-only check: height comparisons false-
  // positive on line-height rounding and made tooltips appear on short rows.
  useEffect(() => {
    const element = labelRef.current
    if (!element) return
    setIsOverflowing(element.offsetWidth < element.scrollWidth)
  }, [option.label])

  return (
    <Box
      onClick={(e) => {
        // The Checkbox owns toggling for clicks on itself/its label; this
        // catches clicks on the row's remaining padding only.
        if (e.target === e.currentTarget) onToggle(option.value)
      }}
      onMouseEnter={(e) => {
        if (isOverflowing) setTooltipAnchor(e.currentTarget)
      }}
      onMouseLeave={() => setTooltipAnchor(null)}
      {...checkboxOptionRowStyles(false, colorScheme)}
    >
      <Checkbox
        checked={isSelected}
        colorScheme={colorScheme}
        label={
          <Box minW={0}>
            <chakra.span ref={labelRef} {...rowLabelStyles}>
              {option.label}
            </chakra.span>
            {option.subtitle && (
              <chakra.span {...rowSubtitleStyles(colorScheme)}>
                {option.subtitle}
              </chakra.span>
            )}
            {option.caption && (
              <chakra.span {...rowCaptionStyles(colorScheme)}>
                {option.caption}
              </chakra.span>
            )}
          </Box>
        }
        onChange={() => onToggle(option.value)}
      />
      {tooltipAnchor && (
        <Tooltip
          anchorEl={tooltipAnchor}
          colorScheme={colorScheme}
          label={option.label}
          placement='top'
        />
      )}
    </Box>
  )
}
